import { ClockIcon, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { formatearFechaYHora } from "@/lib/fechas";

interface BannerDesdeSuUltimaSesionProps {
  /** Cuándo estuvo por última vez en esta pantalla; `null` si no hay registro. */
  ultimaVisita: Date | null;
  /** Lo pendiente, ya dicho en frases cortas. */
  frases: string[];
  textoDelBoton: string;
  onIr: () => void;
  onCerrar: () => void;
}

/**
 * "Desde su última sesión": lo que sigue pendiente, con un botón que lleva a
 * la primera cosa por hacer.
 *
 * Se titula "Desde su última sesión" solo cuando hay una visita anterior que
 * citar; sin ella (primera vez, o el navegador no deja guardarla) dice
 * "Pendiente en este informe" en vez de fingir una sesión que no recordamos.
 */
export function BannerDesdeSuUltimaSesion({
  ultimaVisita,
  frases,
  textoDelBoton,
  onIr,
  onCerrar,
}: BannerDesdeSuUltimaSesionProps) {
  const titulo = ultimaVisita
    ? `Desde su última sesión (${formatearFechaYHora(ultimaVisita.toISOString())})`
    : "Pendiente en este informe";

  return (
    <section
      aria-label={titulo}
      className="bg-divisor flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[0.875rem] py-2.5 pr-3 pl-4"
    >
      <ClockIcon aria-hidden className="text-ink-secundario size-4 shrink-0" />
      <span className="text-ink text-[0.78125rem] font-bold">{titulo}:</span>
      <span className="text-ink-secundario min-w-0 flex-1 text-[0.78125rem] font-semibold text-pretty">
        Falta: {frases.join(" · ")}.
      </span>
      <Button type="button" variant="fuerte" size="sm" className="h-auto rounded-[0.625rem] px-3 py-1.5" onClick={onIr}>
        {textoDelBoton}
      </Button>
      <button
        type="button"
        aria-label="Ocultar"
        onClick={onCerrar}
        className="text-ink-medio hover:bg-divisor-fuerte flex size-7 items-center justify-center rounded-lg transition-colors"
      >
        <XIcon aria-hidden className="size-[0.8125rem]" />
      </button>
    </section>
  );
}
